import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  openClickableSettingModal,
  getSettingModalSelectedPrivacyLevel,
  closeSettingModal,
  setSettingModalPrivacyLevel,
} from './fbSettingsUtils'
import { howPeopleFindYouUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Setting property functions
const TARGET_VALUES = {
  [FacebookSettingKey.WHO_CAN_CONNECT_FRIEND_REQUESTS]:
    selectAudienceUtils.FRIENDS_OF_FRIENDS,
  [FacebookSettingKey.WHO_CAN_CONNECT_FRIENDS_LIST]:
    selectAudienceUtils.FRIENDS,
}

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value || null)
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => {
    const currentSetting = {
      name: value,
      level: selectAudienceUtils.getPrivacySettingFromLabel(value),
    }

    return selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
      currentSetting,
      TARGET_VALUES[key],
    )
  })
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM Helpers

export const FRIEND_REQ_SETTING_LABEL = 'Who can send you friend requests?'
export const FRIENDS_LIST_SETTING_LABEL = 'Who can see your friends list?'
export const FRIENDS_LIST_SETTING_LABEL_PC =
  'Who can see your friends list on your profile?'

// Gather stage

enum StageKey {
  GATHER_SETTING_WHO_CAN_CONNECT = 'GATHER_SETTING_WHO_CAN_CONNECT',
  UPDATE_SETTING_WHO_CAN_CONNECT = 'UPDATE_SETTING_WHO_CAN_CONNECT',
  UNDO_SETTING_WHO_CAN_CONNECT = 'UNDO_SETTING_WHO_CAN_CONNECT',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_WHO_CAN_CONNECT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([FRIEND_REQ_SETTING_LABEL])
      const friendReqSetting = await getSettingModalSelectedPrivacyLevel(
        FRIEND_REQ_SETTING_LABEL,
      )
      await closeSettingModal(FRIEND_REQ_SETTING_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await openClickableSettingModal([FRIENDS_LIST_SETTING_LABEL])
      const friendListSetting = await getSettingModalSelectedPrivacyLevel(
        FRIENDS_LIST_SETTING_LABEL,
      )
      await closeSettingModal(FRIENDS_LIST_SETTING_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      const initialValues = {
        [FacebookSettingKey.WHO_CAN_CONNECT_FRIENDS_LIST]:
          friendListSetting.name,
        [FacebookSettingKey.WHO_CAN_CONNECT_FRIEND_REQUESTS]:
          friendReqSetting.name,
      }

      return updateStoredInitialValues(
        initialValues,
        FBRecommendationKey.FB_WHO_CAN_CONNECT,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_WHO_CAN_CONNECT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_CONNECT_FRIEND_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([FRIEND_REQ_SETTING_LABEL])

        await setSettingModalPrivacyLevel(
          FRIEND_REQ_SETTING_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS_OF_FRIENDS,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_CONNECT_FRIENDS_LIST,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([FRIENDS_LIST_SETTING_LABEL])

        await setSettingModalPrivacyLevel(
          FRIENDS_LIST_SETTING_LABEL,
          isDry,
          selectAudienceUtils.FRIENDS,
        )
      }
      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_WHO_CAN_CONNECT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const prevFriendListValue =
        initialValues[FacebookSettingKey.WHO_CAN_CONNECT_FRIENDS_LIST]
      const prevFriendReqValue =
        initialValues[FacebookSettingKey.WHO_CAN_CONNECT_FRIEND_REQUESTS]

      if (
        settingsSelected[FacebookSettingKey.WHO_CAN_CONNECT_FRIEND_REQUESTS] &&
        prevFriendReqValue
      ) {
        await openClickableSettingModal([FRIEND_REQ_SETTING_LABEL])
        const prevSetting = {
          name: prevFriendReqValue,
          level:
            selectAudienceUtils.getPrivacySettingFromLabel(prevFriendReqValue),
        }
        await setSettingModalPrivacyLevel(
          FRIEND_REQ_SETTING_LABEL,
          isDry,
          prevSetting,
        )
      }

      if (
        settingsSelected[FacebookSettingKey.WHO_CAN_CONNECT_FRIENDS_LIST] &&
        prevFriendListValue
      ) {
        await openClickableSettingModal([FRIENDS_LIST_SETTING_LABEL])
        const prevSetting = {
          name: prevFriendListValue,
          level:
            selectAudienceUtils.getPrivacySettingFromLabel(prevFriendListValue),
        }
        await setSettingModalPrivacyLevel(
          FRIENDS_LIST_SETTING_LABEL,
          isDry,
          prevSetting,
        )
      }
      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
